import { type ApiErrorsResponse, notifyApiErrors } from '@shared-core/runtime/api-errors'
import { getCaptchaSolver } from '@shared-core/runtime/captcha'
import { API_KNOWN_ERRORS } from '@shared-core/services/api/configs/api'
import { CoreLogger } from '@shared-core/services/logger'

const logger = CoreLogger.fork({ name: 'captcha' })

// PoW-капча обычно решается за пару секунд; дольше — считаем, что сервис капчи недоступен.
const CAPTCHA_SOLVE_TIMEOUT_MS = 30_000

// Эти коды запросы с капчей глушат (silentErrorCodes): withCaptcha сначала пробует новый токен
// и показывает ошибку, только если повтор не помог.
export const CAPTCHA_RETRYABLE_ERRORS: readonly string[] = [
  API_KNOWN_ERRORS.CAPTCHA_REQUIRED,
  API_KNOWN_ERRORS.CAPTCHA_INVALID,
]

const hasRetryableCaptchaError = (response: ApiErrorsResponse | null): response is ApiErrorsResponse =>
  Object.values(response?.errors ?? {})
    .flat()
    .some((code) => typeof code === 'string' && CAPTCHA_RETRYABLE_ERRORS.includes(code))

export const solveCaptcha = async (): Promise<string | null> => {
  const solver = getCaptchaSolver()

  if (!solver) return null

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      logger.warn(`Капча не решилась за ${CAPTCHA_SOLVE_TIMEOUT_MS} мс`)
      resolve(null)
    }, CAPTCHA_SOLVE_TIMEOUT_MS)
  })

  try {
    return await Promise.race([
      solver().catch((error: unknown) => {
        logger.warn('Солвер капчи упал', error)
        return null
      }),
      timeout,
    ])
  } finally {
    clearTimeout(timer)
  }
}

// Токен одноразовый: каждый запрос кода получает свежий. Без токена (капча не настроена или
// не решилась) запрос всё равно уходит — пока капча выключена на бэке, он пройдёт.
export const withCaptcha = async <R extends ApiErrorsResponse | null>(
  send: (captchaToken?: string) => Promise<R>,
): Promise<R> => {
  const firstToken = await solveCaptcha()
  const first = await send(firstToken ?? undefined)

  if (!hasRetryableCaptchaError(first)) return first

  const retryToken = await solveCaptcha()

  if (!retryToken) {
    notifyApiErrors(first)
    return first
  }

  const second = await send(retryToken)

  if (hasRetryableCaptchaError(second)) notifyApiErrors(second)

  return second
}
